import React, { useCallback, useState, MutableRefObject, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { SearchField, SearchFieldVariant } from './SearchField';
import { DropDownOverlay } from './DropDownOverlay';
import { Placement } from 'popper.js';
import './SearchBox.less';
import { useListKeyboardNav } from './hooks/useListKeyboardNav';

export type SearchCallback<T> = (text: string) => T[] | Promise<T[]>;

export interface PagingSearchResult<T> {
  items: T[];
  /**
   * if "true" "Load More" button will be visible and will allow to load next page
   */
  hasNext: boolean;
}
export interface PagingSearch<T> {
  pageSize: number;
  searchFn: (text: string, loadedCount: number, pageSize: number) => Promise<PagingSearchResult<T>>;
}

export type SearchEngine<T> = SearchCallback<T> | PagingSearch<T>;

export interface ItemTemplateProps<T> {
  item: T;
  searchText: string;
  index: number;
  /**
   * Indicates, item is currently focused by key
   */
  isFocused: boolean;
  /**
   * To hide layer after choice need to call this method instead of "onItemChose" which is passed to props.
   * I hope it is temporary method until we are agreed on pattern. It seems we need to implement some api, like downshift provided.
   * https://www.npmjs.com/package/downshift
   */
  chooseItemCallback: (item: T) => void;
}

export enum SearchLayerCloseReason {
  emptyText,
  inputBlur,
  itemChosen,
  clickOutside,
}

export interface ISearchBoxProps<T> extends Omit<React.HTMLProps<{}>, 'value'> {
  variant?: SearchFieldVariant;
  showClearIcon?: boolean;
  /**
   * Determines whether to show searchbox or not
   * @default true
   */
  showSearchIcon?: boolean;
  /**
   * Additional css class for "Load More" button
   */
  loadBtnClass?: string;
  /**
   * It can take either Function or Object.
   * Second option allows you to use pagination.
   */
  search: SearchEngine<T>;
  itemTemplate: (props: ItemTemplateProps<T>) => React.ReactNode;
  /**
   * Handle the selection of an item (passed in to help with keyboard navigation.)
   *
   * Remember to use HighlightMatch to highlight search results if you aren't using
   * this to render a component which does it for you.
   */
  onItemChosen: (item: T, element: HTMLLIElement) => void;
  layerProps?: {
    id?: string;
    width?: number | string;
    placement?: Placement;
    className?: string;
    /**
     * data-testid layer. For testing.
     */
    layerTestId?: string;
    offset?: number | string;
  };
  layerControlProps?: {
    isOpen: boolean;
    onClose: (closeReason: SearchLayerCloseReason, event?: MouseEvent | TouchEvent) => void;
    onOpen: () => void;
  };
  inputProps?: {
    searchText: string;
    setSearchText?: (searchText: string) => void;
  };
  noResult?: React.ReactNode;
  /**
   * Only execute overridden blur event handler provided by parent component
   */
  doNotEmitOnBlur?: boolean;
  /**
   * Search will be run only by pressing Enter.
   *
   * @default false
   */
  submitOnEnter?: boolean;
}

/**
 * Returns class name for search box
 * @param isActive indicates that layer is opened
 * @param className additional class name
 */
const getClassName = (isActive: boolean, className?: string) =>
  classNames('dpc-search-box', className, {
    'dpc-search-box--active': isActive,
  });

const getLoadClassName = (className?: string) => classNames('dpc-search-layer__load-more', className);

/**
 * Search box which allows loading items with pagination
 *
 * TODO alex: need to close layer on ESC press
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function SearchBox<T = any>({
  id,
  placeholder,
  search,
  className,
  itemTemplate,
  onFocus,
  onBlur,
  onItemChosen,
  loadBtnClass,
  showSearchIcon = true,
  layerProps,
  noResult,
  layerControlProps: controlProps,
  inputProps,
  showClearIcon = true,
  doNotEmitOnBlur,
  submitOnEnter,
  variant,
}: ISearchBoxProps<T>) {
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadBtnVisible, setLoadMoreVisibility] = useState(false);
  const [localSearchText, setLocalSearchText] = useState<string>('');
  const fieldRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const suggestionsContainerRef: MutableRefObject<HTMLUListElement | null> = useRef(null);
  const [focusedSuggestionIndex, setFocusIndex] = useState(-1);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onOpen = controlProps?.onOpen != null ? controlProps.onOpen : open;
  const onClose = controlProps?.onClose != null ? controlProps.onClose : close;
  const showDropDown = controlProps?.isOpen != null ? controlProps.isOpen : isOpen;

  // we want to be able to control input value from outside when it is needed
  useEffect(() => {
    setLocalSearchText(inputProps?.searchText || '');
  }, [inputProps?.searchText]);

  const setSearchText = inputProps?.setSearchText ?? setLocalSearchText;
  const searchText = inputProps?.searchText ?? localSearchText;

  const handleSearch = useCallback(
    (items: T[]) => {
      setSuggestions(items || []);
      setFocusIndex(-1);
      onOpen();
    },
    [onOpen]
  );

  const handleSearchComplete = useCallback(() => {
    setIsLoading(false);
  }, []);

  //TODO: try to extract to "useSearch" hook
  const onSearch = useCallback(
    (text: string) => {
      if (searchText === text && showDropDown) {
        return;
      }

      setSearchText(text);

      if (!text) {
        handleSearch([]);
        onClose(SearchLayerCloseReason.emptyText);
        return;
      }

      if (search instanceof Function) {
        const result = search(text);
        if ('then' in result) {
          setIsLoading(true);
          result.then(handleSearch).finally(handleSearchComplete);
        } else {
          handleSearch(result);
        }
      } else {
        setIsLoading(true);
        search
          .searchFn(text, 0, search.pageSize)
          .then(result => {
            setLoadMoreVisibility(result.hasNext);
            handleSearch(result.items);
          })
          .finally(handleSearchComplete);
      }
    },
    [searchText, showDropDown, setSearchText, search, handleSearch, onClose, handleSearchComplete]
  );

  const handleChose = useCallback(
    (item: T) => {
      const index = suggestions.findIndex(suggestion => suggestion === item);
      const element = suggestionsContainerRef.current?.children[index] as HTMLLIElement;

      onItemChosen(item, element);
      onClose(SearchLayerCloseReason.itemChosen);
    },
    [onClose, onItemChosen, suggestions]
  );

  const onLoadClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // to prevent hiding overlay
      e.stopPropagation();

      if ('searchFn' in search) {
        search
          .searchFn(searchText, suggestions.length, search.pageSize)
          .then(result => {
            setLoadMoreVisibility(result.hasNext);
            handleSearch([...suggestions, ...result.items]);
          })
          .finally(handleSearchComplete);
      }
    },
    [handleSearch, handleSearchComplete, search, searchText, suggestions]
  );

  /**
   * TODO alex: there is no ability to navigate to "Load More" button
   */
  const { keyboardNavHandler } = useListKeyboardNav<T>(focusedSuggestionIndex, suggestions, setFocusIndex, handleChose);

  const onClear = useCallback(() => onSearch(''), [onSearch]);

  const handleBlur = (e: React.FocusEvent) => {
    onClose(SearchLayerCloseReason.inputBlur);
    onBlur && onBlur(e);
  };

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (submitOnEnter) {
        const newValue = (e.target as HTMLInputElement).value;

        if (!newValue && showDropDown) {
          handleSearch([]);
          onClose(SearchLayerCloseReason.emptyText);
          return;
        }
      }

      keyboardNavHandler(e);
    },
    [submitOnEnter, showDropDown, keyboardNavHandler, handleSearch, onClose]
  );

  return (
    <>
      <SearchField
        id={id}
        variant={variant}
        ref={fieldRef}
        onSearch={onSearch}
        onClear={onClear}
        placeholder={placeholder}
        value={searchText}
        className={getClassName(showDropDown, className)}
        showSearchIcon={showSearchIcon}
        onBlur={handleBlur}
        onFocus={onFocus}
        showClearIcon={showClearIcon}
        onKeyUp={handleKeyUp}
        doNotEmitOnBlur={doNotEmitOnBlur}
        isLoading={isLoading}
        submitOnEnter={submitOnEnter}
      />
      <DropDownOverlay
        id={layerProps?.id}
        className={layerProps?.className}
        sourceRef={fieldRef}
        isOpen={showDropDown}
        onClose={useCallback(event => onClose(SearchLayerCloseReason.clickOutside, event), [onClose])}
        data-testid={layerProps?.layerTestId}
        callOutWidth={layerProps?.width}
        placement={layerProps?.placement}
        offset={layerProps?.offset}
      >
        <ul className="dpc-searchbox__suggestion-list" ref={suggestionsContainerRef}>
          {(suggestions == null || suggestions.length === 0) && noResult != null && noResult}
          {suggestions.map((item, i) =>
            itemTemplate({
              item,
              searchText,
              index: i,
              isFocused: i === focusedSuggestionIndex,
              chooseItemCallback: handleChose,
            })
          )}
        </ul>
        {isLoadBtnVisible && suggestions.length > 0 && (
          <button className={getLoadClassName(loadBtnClass)} onClick={onLoadClick}>
            More...
          </button>
        )}
      </DropDownOverlay>
    </>
  );
}
